import React, { useState, useEffect, useRef, useLayoutEffect, useContext } from 'react'
import { HotTable } from '@handsontable/react'
import { HotContainer } from '../commom'
import DateService from 'live/services/date'
import i18n from 'live/services/i18n'
import { Actions } from '../styles/styles'
import LiveTooltip from 'live/components/ui/tooltip'
import _, { cloneDeep, result, set } from 'lodash'
import ReactDOM from 'react-dom'
import { canEditDvDData } from '../permission/Permissions'
import { DvDDataContext } from '../context/DvDDataContext'
import {
    getElementForCell,
    addCalculatedDvDColumns,
    getDataFromHotTable,
    addDvDChangeLogs,
    getRowCountFromHotTable,
    roundTime,
    saveDataset,
    classNames
} from '../functions/functions'

export interface TableDataProps {
    selected: {
        asset: {
            id: string
            name: string
        }
    }
    datasetType: string
}

export const TableData = (props: TableDataProps) => {
    const refTable = React.useRef<HotTable>(null)
    const { id, name } = props.selected?.asset

    const {
        heightOfCentralComponents,
        tableData,
        setTableData,
        unSavedData,
        setUnSavedData,
        loadingDataset,
        setDataRowCount,
        dataRowCount,
        setLoadingDataset,
        setlastDataEditTime,
        isSyncForced,
        showCopyDataForm,
        setShowCopyDataForm,
        setCopyMaxDate
    } = useContext(DvDDataContext)

    return (
        <>
            <HotContainer>
                <HotTable
                    ref={refTable}
                    data={tableData}
                    settings={{
                        colHeaders: true,
                        rowHeaders: true,
                        readOnlyCellClassName: 'is-readOnly',
                        columns: [
                            { data: 'datetime', readOnly: true },
                            { data: 'days_cum', readOnly: true },
                            {
                                data: 'timestamp',
                                readOnly: !canEditDvDData() || loadingDataset || isSyncForced,
                                validator: 'numeric',
                                allowInvalid: false,
                                allowEmpty: false
                            },
                            {
                                data: 'bit_depth_ft',
                                readOnly: !canEditDvDData() || loadingDataset || isSyncForced,
                                validator: 'numeric',
                                allowInvalid: false,
                                allowEmpty: false
                            },
                            {
                                data: 'hole_depth_ft',
                                readOnly: !canEditDvDData() || loadingDataset || isSyncForced,
                                validator: 'numeric',
                                allowInvalid: false,
                                allowEmpty: false
                            },
                            {
                                data: 'rigstate',
                                readOnly: !canEditDvDData() || loadingDataset || isSyncForced
                            },
                            {
                                data: function () {
                                    return {
                                        changeBy: arguments[0]['changeLog']
                                            ? arguments[0]['changeLog']['changeBy']
                                            : 'System',
                                        changeAt: arguments[0]['changeLog'] ? arguments[0]['changeLog']['changeAt'] : 0,
                                        dataSource: arguments[0]['DataSource'] ? arguments[0]['DataSource'] : undefined,
                                        isWellBreak:
                                            arguments[0]['rigstate'] &&
                                            arguments[0]['rigstate']
                                                .toLocaleString()
                                                .toLowerCase()
                                                .includes('well break')
                                                ? true
                                                : undefined
                                    }
                                },
                                renderer: function renderCellToolTipButton(
                                    instance,
                                    td,
                                    row,
                                    col,
                                    prop,
                                    value,
                                    cellProperties
                                ) {
                                    try {
                                        if (value !== undefined && value !== '') {
                                            if (
                                                value &&
                                                ((value.changeBy !== 'System' && value.changeBy !== '') ||
                                                    value.dataSource ||
                                                    value.isWellBreak)
                                            ) {
                                                const container = getElementForCell(row, col)
                                                const toolTipChangeLog =
                                                    value.changeBy !== 'System' && value.changeBy !== ''
                                                        ? 'Edited by ' +
                                                          value.changeBy +
                                                          ' at ' +
                                                          DateService.getMoment(value.changeAt).format(
                                                              'YYYY-MM-DD HH:mm:ss Z'
                                                          )
                                                        : undefined

                                                const toolTipDataSource = value.dataSource
                                                    ? 'Data Copied from ' + value.dataSource.split(':')[1]
                                                    : undefined

                                                const tooltipWellBreak = value.isWellBreak
                                                    ? 'Added Well Break and the time between current day and previous day is not considered'
                                                    : undefined

                                                ReactDOM.render(
                                                    <div>
                                                        {tooltipWellBreak && (
                                                            <LiveTooltip
                                                                position="left"
                                                                content={i18n(tooltipWellBreak)}
                                                            >
                                                                <i className="fa fa-chain-broken"></i>
                                                            </LiveTooltip>
                                                        )}
                                                        {`  `}
                                                        {toolTipDataSource && (
                                                            <LiveTooltip
                                                                position="left"
                                                                content={i18n(toolTipDataSource)}
                                                            >
                                                                <i className="fa fa-copy"></i>
                                                            </LiveTooltip>
                                                        )}
                                                        {`  `}
                                                        {toolTipChangeLog && (
                                                            <LiveTooltip
                                                                position="left"
                                                                content={i18n(toolTipChangeLog)}
                                                            >
                                                                <i className="fa fa-info-circle"></i>
                                                            </LiveTooltip>
                                                        )}
                                                    </div>,
                                                    container
                                                )
                                                while (td.firstChild) {
                                                    td.removeChild(td.firstChild)
                                                }
                                                td.appendChild(container)

                                                return td
                                            }
                                        }
                                        td.innerHTML = '<div></div>'
                                        return td
                                    } catch {
                                        console.log('Rendering error')
                                    }
                                    return td
                                },
                                width: 45,
                                readOnly: true
                            }
                        ],
                        colHeaders: [
                            'Date/Time',
                            'Cum. Days',
                            'timestamp',
                            'Bit Depth (ft)',
                            'Hole Depth (ft)',
                            'Rigstate',
                            ''
                        ],
                        allowEmpty: true,
                        allowInsertColumn: false,
                        autoColumnSize: true,
                        columnSorting: {
                            sortEmptyCells: true,
                            initialConfig: {
                                column: 2,
                                sortOrder: 'asc'
                            }
                        },
                        stretchH: 'all',
                        autoRowSize: true,
                        beforeChange: (changes, source) => {},
                        afterChange: (changes, source) => {
                            const rowCount = getRowCountFromHotTable(refTable)
                            if (source === 'loadData') {
                                //setLoadingDataset(false)
                            }
                            if (source === 'edit' || source === 'CopyPaste.paste' || source === 'Autofill.fill') {
                                const newLocalData = addCalculatedDvDColumns(getDataFromHotTable(refTable))
                                let changeLogData = cloneDeep(newLocalData)
                                changes.forEach(([row, prop, oldValue, newValue]) => {
                                    if (String(oldValue) !== String(newValue)) {
                                        setUnSavedData(true)
                                        changeLogData = addDvDChangeLogs(changeLogData, row)
                                    }
                                })
                                try {
                                    setTableData(changeLogData)
                                } catch {}
                            }
                            setDataRowCount(rowCount)
                        },
                        afterRemoveRow: (index, amount) => {
                            setDataRowCount(dataRowCount - amount)
                            setUnSavedData(true)
                            try {
                                setTableData(addCalculatedDvDColumns(getDataFromHotTable(refTable)))
                            } catch {}
                        },
                        afterCreateRow: (index, amount) => {
                            const rowCount = getRowCountFromHotTable(refTable)
                            if (rowCount === 0) {
                                refTable.current?.hotInstance.setDataAtCell(index, 2, roundTime(Date.now()))
                            } else {
                                const hotTableData = getDataFromHotTable(refTable)
                                if (index === 0) {
                                    refTable.current?.hotInstance.setDataAtCell(
                                        index,
                                        2,
                                        Number(hotTableData[index + 1]?.timestamp) - 3600000
                                    )
                                } else if (index === rowCount) {
                                    refTable.current?.hotInstance.setDataAtCell(
                                        index,
                                        2,
                                        Number(hotTableData[index - 1]?.timestamp) + 3600000
                                    )
                                } else {
                                    refTable.current?.hotInstance.setDataAtCell(
                                        index,
                                        2,
                                        Number(hotTableData[index - 1]?.timestamp) +
                                            (Number(hotTableData[index + 1]?.timestamp) -
                                                Number(hotTableData[index - 1]?.timestamp)) /
                                                2
                                    )
                                }
                            }
                        },
                        afterLoadData: firstTime => {},
                        contextMenu: {
                            callback(key, selection, clickEvent) {},
                            items: {
                                copy_well: {
                                    name() {
                                        return '<b>Copy From Other Well Up To Here</b>'
                                    },
                                    hidden() {
                                        return this.getSelectedLast()[1] == 0
                                    },
                                    callback(key, selection, clickEvent) {
                                        const t = refTable.current?.hotInstance.getDataAtCell(selection[0].start.row, 2)
                                        if (t && t > 0) {
                                            setCopyMaxDate(t)
                                        } else {
                                            const sourceData = getDataFromHotTable(refTable)
                                            if (sourceData && sourceData.length > 0) {
                                                setCopyMaxDate(sourceData[0]?.timestamp)
                                            }
                                        }
                                        setShowCopyDataForm(true)
                                    }
                                },
                                sepratorOne: '---------',
                                row_break: {
                                    name() {
                                        return '<b>Add Wellbreak</b>'
                                    },
                                    hidden() {
                                        return this.getSelectedLast()[1] == 0
                                    },
                                    callback(key, selection, clickEvent) {
                                        if (selection.length == 1) {
                                            for (let i = selection[0].start.row; i <= selection[0].end.row; i++) {
                                                const existingRigState = refTable.current?.hotInstance.getDataAtCell(
                                                    i,
                                                    5
                                                )
                                                refTable.current?.hotInstance.setDataAtCell(
                                                    i,
                                                    5,
                                                    `${existingRigState}:Well Break`
                                                )
                                            }
                                        }
                                    },
                                    disabled: function () {
                                        const selectionRows = this.getSelectedRangeLast()
                                        let disable = true
                                        const selectedRowRigState = this.getDataAtCell(selectionRows.from.row, 5)
                                        disable = selectedRowRigState
                                            .toLocaleString()
                                            .toLowerCase()
                                            .includes('well break')

                                        return selectionRows.from.row !== selectionRows.to.row || disable
                                    }
                                },
                                row_break_remove: {
                                    name() {
                                        return '<b>Remove Wellbreak</b>'
                                    },
                                    hidden() {
                                        return this.getSelectedLast()[1] == 0
                                    },
                                    callback(key, selection, clickEvent) {
                                        if (selection.length == 1) {
                                            for (let i = selection[0].start.row; i <= selection[0].end.row; i++) {
                                                const existingRigState = refTable.current?.hotInstance.getDataAtCell(
                                                    i,
                                                    5
                                                )
                                                refTable.current?.hotInstance.setDataAtCell(
                                                    i,
                                                    5,
                                                    existingRigState.split(':')[0]
                                                )
                                            }
                                        }
                                    },
                                    disabled: function () {
                                        const selectionRows = this.getSelectedRangeLast()
                                        let disable = true
                                        const selectedRowRigState = this.getDataAtCell(selectionRows.from.row, 5)
                                        disable = !selectedRowRigState
                                            .toLocaleString()
                                            .toLowerCase()
                                            .includes('well break')

                                        return selectionRows.from.row !== selectionRows.to.row || disable
                                    }
                                },
                                sepratorTwo: '---------',
                                row_above: {
                                    name() {
                                        return 'Insert row above'
                                    }
                                },
                                row_below: {
                                    name() {
                                        return 'Insert row below'
                                    }
                                },
                                remove_row: {
                                    name() {
                                        return 'Remove row(s)'
                                    }
                                }
                            }
                        },
                        height: heightOfCentralComponents
                    }}
                ></HotTable>
            </HotContainer>
            <Actions>
                {canEditDvDData() && (
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            saveDataset(
                                unSavedData,
                                refTable,
                                id,
                                props.datasetType,
                                setLoadingDataset,
                                setUnSavedData,
                                setlastDataEditTime
                            )
                        }
                        disabled={loadingDataset || isSyncForced || !unSavedData}
                    >
                        Save
                    </button>
                )}
            </Actions>
        </>
    )
}
